import { id } from '@ethersproject/hash';
import { TransactionRequest } from '@ethersproject/providers';
import invariant from 'tiny-invariant';
import { CurrencyAmount, Percent } from '../amounts';
import { TradeType } from '../constants/constants';
import { BaseTrade } from '../types';

export enum Trade0xLiquiditySource {
  ZeroEx = '0x',
  Native = 'Native',
  Uniswap = 'Uniswap',
  UniswapV2 = 'Uniswap_V2',
  Eth2Dai = 'Eth2Dai',
  Kyber = 'Kyber',
  Curve = 'Curve',
  LiquidityProvider = 'LiquidityProvider',
  MultiBridge = 'MultiBridge',
  Balancer = 'Balancer',
  BalancerV2 = 'Balancer_V2',
  Cream = 'CREAM',
  Bancor = 'Bancor',
  BancorV3 = 'BancorV3',
  MakerPsm = 'MakerPsm',
  MStable = 'mStable',
  Mooniswap = 'Mooniswap',
  MultiHop = 'MultiHop',
  Shell = 'Shell',
  Swerve = 'Swerve',
  SnowSwap = 'SnowSwap',
  SushiSwap = 'SushiSwap',
  Dodo = 'DODO',
  DodoV2 = 'DODO_V2',
  CryptoCom = 'CryptoCom',
  Linkswap = 'Linkswap',
  KyberDmm = 'KyberDMM',
  Smoothy = 'Smoothy',
  Synapse = 'Synapse',
  Component = 'Component',
  Saddle = 'Saddle',
  XSigma = 'xSigma',
  UniswapV3 = 'Uniswap_V3',
  CurveV2 = 'Curve_V2',
  Lido = 'Lido',
  ShibaSwap = 'ShibaSwap',
  Clipper = 'Clipper',
  JetSwap = 'JetSwap',
  /**
   * BSC only
   */
  PancakeSwap = 'PancakeSwap',
  PancakeSwapV2 = 'PancakeSwap_V2',
  BakerySwap = 'BakerySwap',
  Nerve = 'Nerve',
  Belt = 'Belt',
  Ellipsis = 'Ellipsis',
  ApeSwap = 'ApeSwap',
  CafeSwap = 'CafeSwap',
  CheeseSwap = 'CheeseSwap',
  JulSwap = 'JulSwap',
  ACryptos = 'ACryptoS',
  BiSwap = 'BiSwap',
  KnightSwap = 'KnightSwap',
  MDex = 'MDex',
  /**
   * Polygon only
   */
  QuickSwap = 'QuickSwap',
  ComethSwap = 'ComethSwap',
  Dfyn = 'Dfyn',
  WaultSwap = 'WaultSwap',
  Polydex = 'Polydex',
  FirebirdOneSwap = 'FirebirdOneSwap',
  IronSwap = 'IronSwap',
  AaveV2 = 'Aave_V2',
  MeshSwap = 'MeshSwap',
  /**
   * AVALANCHE only
   */
  Pangolin = 'Pangolin',
  TraderJoe = 'TraderJoe',
  GMX = 'GMX',
  Platypus = 'Platypus',
  /**
   * Fantom only
   */
  Beethovenx = 'Beethovenx',
  MorpheusSwap = 'MorpheusSwap',
  SpiritSwap = 'SpiritSwap',
  Geist = 'Geist',
  SpookySwap = 'SpookySwap',
  Yoshi = 'Yoshi',
  /**
   * Celo only
   */
  UbeSwap = 'UbeSwap',
  MobiusMoney = 'MobiusMoney',
  /**
   * Optimism
   */
  Velodrome = 'Velodrome',
}

export interface Trade0xSwapProportion {
  /**
   * Liquidity source provider info
   */
  name: Trade0xLiquiditySource;
  /**
   * In percentage
   */
  proportion: number;
}

export interface Trade0xSwapParams {
  tradeType: TradeType;
  // Total Sell Amount (includes all fees)
  inputAmount: CurrencyAmount;
  // Number of tokens received.
  outputAmount: CurrencyAmount;
  // Referral and plasma fees
  feeAmount: CurrencyAmount;
  // The address for the token approval. If undefined, approval is not needed.
  allowanceTarget?: string;
  // On which DEX the exchange will be made, the proportions as a percentage.
  proportions: Trade0xSwapProportion[];
  // By how many percent the rate of purchased tokens is less than the rate of purchased tokens.
  priceImpact?: Percent;
  // Transaction data, for making an exchange. You need to sign and send it to the blockchain.
  tx?: TransactionRequest;
}

export class Trade0xSwap extends BaseTrade {
  /**
   * Trade ID. Trade hash by input and output amounts and proportions
   */
  public readonly id: string;

  public readonly feeAmount: CurrencyAmount;

  public readonly allowanceTarget?: string;

  public readonly proportions: Trade0xSwapProportion[];

  public readonly priceImpact?: Percent;

  public readonly tx?: TransactionRequest;

  constructor(params: Trade0xSwapParams) {
    invariant(params.tradeType === TradeType.EXACT_INPUT || params.tradeType === TradeType.EXACT_OUTPUT, 'Unsupported trade type');
    super(params.tradeType, params.inputAmount, params.outputAmount);

    this.feeAmount = params.feeAmount;

    // Build trade hash
    const proportions = params.proportions
      .sort((p0, p1) => p1.proportion - p0.proportion)
      .map(p => `${p.name}-${p.proportion}`)
      .join('-');
    this.id = id(`${this.inputAmount.raw.toString()}|${this.outputAmount.raw.toString()}|${proportions}`);

    this.proportions = params.proportions;
    this.allowanceTarget = params.allowanceTarget;
    this.priceImpact = params.priceImpact;
    this.tx = params.tx;
  }
}
